/**
 * Log Viewer - Column Picker
 *
 * Allows users to show/hide columns in the log viewer
 */

import React, { useState } from "react";
import { LogColumn, ColumnConfig } from "./types";

export interface ColumnPickerProps {
  /** Available columns */
  columns: ColumnConfig[];
  /** Currently visible columns */
  visibleColumns: Set<LogColumn>;
  /** Callback when visibility changes */
  onVisibilityChange: (column: LogColumn, visible: boolean) => void;
  /** Callback to switch to parsed view */
  onSwitchToParsed?: () => void;
  /** Callback to switch to raw view */
  onSwitchToRaw?: () => void;
}

export function ColumnPicker(props: ColumnPickerProps) {
  const { columns, visibleColumns, onVisibilityChange, onSwitchToParsed, onSwitchToRaw } = props;
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (column: LogColumn) => {
    const isVisible = visibleColumns.has(column);
    onVisibilityChange(column, !isVisible);
  };

  return (
    <div className="column-picker">
      <button
        className="column-picker__button"
        onClick={() => setIsOpen(!isOpen)}
        title="Select columns to display"
      >
        Columns ▾
      </button>

      {isOpen && (
        <>
          <div
            className="column-picker__backdrop"
            onClick={() => setIsOpen(false)}
          />
          <div className="column-picker__menu">
            <div className="column-picker__header">
              <span>Display Columns</span>
            </div>

            {/* View Presets */}
            {(onSwitchToParsed || onSwitchToRaw) && (
              <div className="column-picker__presets">
                {onSwitchToParsed && (
                  <button
                    className="column-picker__preset-button"
                    onClick={() => {
                      onSwitchToParsed();
                      setIsOpen(false);
                    }}
                  >
                    Parsed View
                  </button>
                )}
                {onSwitchToRaw && (
                  <button
                    className="column-picker__preset-button"
                    onClick={() => {
                      onSwitchToRaw();
                      setIsOpen(false);
                    }}
                  >
                    Raw View
                  </button>
                )}
              </div>
            )}

            <div className="column-picker__items">
              {columns.map((col) => {
                const isVisible = visibleColumns.has(col.column);
                return (
                  <label key={col.column} className="column-picker__item">
                    <input
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => handleToggle(col.column)}
                    />
                    <span>{col.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
